import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { visionBoardAPI, habitAPI } from '../lib/supabase';

const {
  FiEye, FiPlus, FiEdit2, FiTrash2, FiImage, FiQuote, FiTarget, FiHeart, FiGrid, FiCheck,
  FiXCircle, FiCalendar, FiFileText, FiList, FiCompass, FiClock, FiStar, FiLink, FiPercent,
  FiAlertCircle, FiTrendingUp, FiActivity, FiX, FiCheckCircle, FiXOctagon, FiAward, FiZap,
  FiMap, FiBookOpen, FiSun, FiMoon, FiSmile, FiFrown, FiMeh, FiArrowRight, FiChevronLeft,
  FiChevronRight, FiMinusCircle
} = FiIcons;

const VisionBoard = () => {
  const [visionItems, setVisionItems] = useState([]);
  const [habits, setHabits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentStep, setCurrentStep] = useState(0);
  const [showActionForm, setShowActionForm] = useState(false);
  const [showSetbackForm, setShowSetbackForm] = useState(false);
  const [showTimelineView, setShowTimelineView] = useState(false);
  const [selectedVision, setSelectedVision] = useState(null);
  const [actionFormData, setActionFormData] = useState({
    text: '',
    mood: 'good',
    energy_level: 5,
    confidence: 5,
    notes: '',
    date: format(new Date(), 'yyyy-MM-dd')
  });
  const [setbackFormData, setSetbackFormData] = useState({
    text: '',
    mood: 'challenging',
    impact_level: 3,
    lesson_learned: '',
    recovery_plan: '',
    date: format(new Date(), 'yyyy-MM-dd')
  });
  
  const [formData, setFormData] = useState({
    type: 'goal',
    title: '',
    content: '',
    image_url: '',
    category: 'personal',
    linked_habits: [],
    is_achieved: false,
    target_date: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    purpose: '',
    importance: 5,
    timeframe: 'medium',
    progress: 0,
    action_steps: [],
    predicted_actions: [],
    predicted_obstacles: [],
    failures: [],
    adjustments: [],
    journey_entries: [],
    milestones: []
  });

  const categories = [
    { id: 'all', name: 'All', color: 'forge' },
    { id: 'personal', name: 'Personal', color: 'purple' },
    { id: 'career', name: 'Career', color: 'blue' },
    { id: 'health', name: 'Health', color: 'emerald' },
    { id: 'relationships', name: 'Social', color: 'pink' },
    { id: 'financial', name: 'Money', color: 'forge' },
    { id: 'creative', name: 'Creative', color: 'indigo' }
  ];

  const visionTypes = [
    { id: 'goal', name: 'Goal', icon: FiTarget, description: 'Set a specific goal' },
    { id: 'affirmation', name: 'Affirmation', icon: FiQuote, description: 'Positive statement' },
    { id: 'image', name: 'Image', icon: FiImage, description: 'Inspiring image' }
  ];

  const timeframes = [
    { id: 'short', name: 'Short-term', description: '1-3 months', icon: FiClock },
    { id: 'medium', name: 'Medium-term', description: '3-12 months', icon: FiCalendar },
    { id: 'long', name: 'Long-term', description: '1+ years', icon: FiTarget }
  ];

  const formSteps = [
    { title: 'Type', icon: FiGrid, description: 'Choose vision type' },
    { title: 'Details', icon: FiFileText, description: 'Add core information' },
    { title: 'Purpose', icon: FiCompass, description: 'Why it matters' },
    { title: 'Predict Path', icon: FiActivity, description: 'Plan your journey' },
    { title: 'Habits', icon: FiList, description: 'Link to habits' }
  ];

  const moods = [
    { id: 'excellent', name: 'Excellent', emoji: '🌟', color: 'from-forge-400 to-orange-500' },
    { id: 'good', name: 'Good', emoji: '😊', color: 'from-emerald-400 to-green-500' },
    { id: 'neutral', name: 'Neutral', emoji: '😐', color: 'from-gray-400 to-slate-500' },
    { id: 'challenging', name: 'Challenging', emoji: '😔', color: 'from-blue-400 to-indigo-500' },
    { id: 'difficult', name: 'Difficult', emoji: '😰', color: 'from-danger-400 to-pink-500' }
  ];

  const milestoneTemplates = [
    { threshold: 25, title: 'First Steps', description: 'You\'ve taken your first steps!', icon: '👟', color: 'from-blue-400 to-indigo-500' },
    { threshold: 50, title: 'Halfway Hero', description: 'You\'re halfway there!', icon: '🚀', color: 'from-purple-400 to-violet-500' },
    { threshold: 75, title: 'Almost There', description: 'The finish line is in sight!', icon: '🏁', color: 'from-orange-400 to-red-500' },
    { threshold: 100, title: 'Vision Achieved', description: 'Congratulations! You did it!', icon: '🏆', color: 'from-forge-400 to-gold-500' }
  ];

  // Create example vision items
  const createExampleItems = () => {
    return [
      {
        id: 'example-1',
        type: 'goal',
        title: 'Complete a Marathon',
        content: 'Train and complete a full marathon within the next 6 months to challenge myself physically',
        category: 'health',
        is_achieved: false,
        target_date: format(new Date(Date.now() + 180 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
        purpose: 'To challenge myself physically and prove I can achieve difficult goals with discipline and persistence',
        linked_habits: [1, 3],
        created_at: new Date().toISOString(),
        importance: 5,
        timeframe: 'medium',
        progress: 25,
        action_steps: [
          { id: 1, text: 'Started running 3 times per week', completed: true, date: '2024-01-10' },
          { id: 2, text: 'Completed first 10K run', completed: true, date: '2024-01-15' },
          { id: 3, text: 'Join local running group', completed: false, date: null }
        ],
        predicted_actions: [
          { id: 101, text: 'Complete half-marathon training program', completed: false },
          { id: 102, text: 'Gradually increase weekly mileage', completed: false },
          { id: 103, text: 'Research and sign up for a marathon event', completed: false }
        ],
        predicted_obstacles: [
          { id: 201, text: 'Potential injury from overtraining', mitigated: false },
          { id: 202, text: 'Time management challenges', mitigated: false },
          { id: 203, text: 'Weather conditions affecting training', mitigated: false }
        ],
        failures: [
          { id: 1, text: 'Missed training for a week due to illness', date: '2024-01-08', lesson_learned: 'Need to build recovery time into training schedule' }
        ],
        adjustments: [
          { id: 1, text: 'Added rest days and cross-training to prevent injury', date: '2024-01-12' }
        ],
        journey_entries: [
          { 
            id: 1, 
            type: 'action', 
            text: 'Started running 3 times per week', 
            mood: 'good', 
            energy_level: 4, 
            confidence: 4, 
            notes: 'Feeling motivated to start this journey!', 
            date: '2024-01-10' 
          }
        ],
        milestones: [
          { threshold: 25, unlocked: true, date: '2024-01-15' }
        ]
      },
      {
        id: 'example-2',
        type: 'affirmation',
        title: 'Daily Inspiration',
        content: 'I am capable of achieving my goals through consistent daily action and perseverance.',
        category: 'personal',
        is_achieved: false,
        target_date: format(new Date(), 'yyyy-MM-dd'),
        purpose: 'To maintain a positive mindset and reinforce my belief in my abilities',
        linked_habits: [],
        created_at: new Date().toISOString(),
        importance: 4,
        timeframe: 'short',
        progress: 100,
        action_steps: [],
        predicted_actions: [],
        predicted_obstacles: [],
        failures: [],
        adjustments: [],
        journey_entries: [],
        milestones: [
          { threshold: 25, unlocked: true, date: '2024-01-05' },
          { threshold: 50, unlocked: true, date: '2024-01-10' },
          { threshold: 75, unlocked: true, date: '2024-01-15' },
          { threshold: 100, unlocked: true, date: '2024-01-20' }
        ]
      },
      {
        id: 'example-3',
        type: 'image',
        title: 'Dream Home',
        content: 'My future beach house where I can relax and find peace away from the busy city life',
        image_url: 'https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?w=800&auto=format&fit=crop',
        category: 'financial',
        is_achieved: false,
        target_date: format(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
        purpose: 'To focus on long-term financial goals and what they will enable in my life',
        linked_habits: [],
        created_at: new Date().toISOString(),
        importance: 5,
        timeframe: 'long',
        progress: 15,
        action_steps: [
          { id: 1, text: 'Opened high-yield savings account', completed: true, date: '2024-01-05' },
          { id: 2, text: 'Set up automatic savings transfer', completed: true, date: '2024-01-07' },
          { id: 3, text: 'Research coastal property markets', completed: false, date: null }
        ],
        predicted_actions: [
          { id: 104, text: 'Meet with financial advisor to create investment plan', completed: false },
          { id: 105, text: 'Research mortgage pre-approval process', completed: false },
          { id: 106, text: 'Create vision board with specific house features', completed: false }
        ],
        predicted_obstacles: [
          { id: 204, text: 'Housing market price increases', mitigated: false },
          { id: 205, text: 'Unexpected major expenses', mitigated: false },
          { id: 206, text: 'Finding the right location within budget', mitigated: false }
        ],
        failures: [
          { id: 1, text: 'Overspent on vacation, reduced savings goal', date: '2024-01-20', lesson_learned: 'Need better budget discipline and emergency fund' }
        ],
        adjustments: [
          { id: 1, text: 'Created separate vacation fund to protect house savings', date: '2024-01-22' }
        ],
        journey_entries: [
          { 
            id: 1, 
            type: 'action', 
            text: 'Opened high-yield savings account', 
            mood: 'excellent', 
            energy_level: 5, 
            confidence: 5, 
            notes: 'Excited to start saving for my dream!', 
            date: '2024-01-05' 
          },
          { 
            id: 2, 
            type: 'setback', 
            text: 'Overspent on vacation', 
            mood: 'challenging', 
            impact_level: 3, 
            lesson_learned: 'Need better budget discipline', 
            recovery_plan: 'Create separate vacation fund', 
            date: '2024-01-20' 
          }
        ],
        milestones: []
      }
    ];
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load data from API
      const [visionResult, habitsResult] = await Promise.all([
        visionBoardAPI.getVisionItems(),
        habitAPI.getHabits()
      ]);

      let visionData = visionResult.data || [];

      // Always show example items for demo purposes
      if (visionData.length === 0) {
        visionData = createExampleItems();
        console.log('Created example vision items:', visionData);
      }

      setVisionItems(visionData);
      setHabits(habitsResult.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      // Always fallback to example items
      const exampleItems = createExampleItems();
      setVisionItems(exampleItems);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.title.trim()) {
      alert('Please enter a title for your vision');
      return;
    }
    if (!formData.content.trim()) {
      alert('Please enter content for your vision');
      return;
    }

    try {
      // Prepare the data for submission
      const submitData = {
        ...formData,
        title: formData.title.trim(),
        content: formData.content.trim(),
        image_url: formData.image_url.trim(),
        purpose: formData.purpose.trim(),
        created_at: new Date().toISOString()
      };

      let result;
      if (editingItem) {
        result = await visionBoardAPI.updateVisionItem(editingItem.id, submitData);
        setVisionItems(prev => prev.map(item => item.id === editingItem.id ? result.data : item));
      } else {
        // Generate a unique ID for new items
        const newItem = {
          ...submitData,
          id: `vision-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          created_at: new Date().toISOString()
        };

        // Add to UI immediately
        setVisionItems(prev => [newItem, ...prev]);

        // Try to save to backend (will work when Supabase is connected)
        try {
          result = await visionBoardAPI.createVisionItem(submitData);
          if (result.data) {
            // Replace temp item with real one if backend save succeeds
            setVisionItems(prev => prev.map(item => item.id === newItem.id ? result.data : item));
          }
        } catch (backendError) {
          console.log('Backend save failed, keeping local item:', backendError);
          // Item is already in UI, so we continue
        }
      }

      // Close form and reset
      setShowForm(false);
      setEditingItem(null);
      resetForm();
    } catch (error) {
      console.error('Error saving vision item:', error);
      alert('There was an error saving your vision. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'goal',
      title: '',
      content: '',
      image_url: '',
      category: 'personal',
      linked_habits: [],
      is_achieved: false,
      target_date: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
      purpose: '',
      importance: 5,
      timeframe: 'medium',
      progress: 0,
      action_steps: [],
      predicted_actions: [],
      predicted_obstacles: [],
      failures: [],
      adjustments: [],
      journey_entries: [],
      milestones: []
    });
    setCurrentStep(0);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      ...item,
      linked_habits: item.linked_habits || [],
      importance: item.importance || 5,
      timeframe: item.timeframe || 'medium',
      progress: item.progress || 0,
      action_steps: item.action_steps || [],
      predicted_actions: item.predicted_actions || [],
      predicted_obstacles: item.predicted_obstacles || [],
      failures: item.failures || [],
      adjustments: item.adjustments || [],
      journey_entries: item.journey_entries || [],
      milestones: item.milestones || []
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this vision?')) return;

    // Optimistically remove from UI
    setVisionItems(prev => prev.filter(item => item.id !== id));

    try {
      await visionBoardAPI.deleteVisionItem(id);
    } catch (error) {
      console.error('Error deleting vision item:', error);
      // Restore item if deletion fails
      loadData();
    }
  };

  const toggleAchieved = async (item) => {
    // Optimistically update UI
    setVisionItems(prev => prev.map(i => i.id === item.id ? {...i, is_achieved: !i.is_achieved} : i));

    // If marking as achieved and not already achieved, show timeline
    if (!item.is_achieved) {
      setSelectedVision(item);
      setShowTimelineView(true);
    }

    try {
      await visionBoardAPI.updateVisionItem(item.id, {...item, is_achieved: !item.is_achieved});
    } catch (error) {
      console.error('Error updating achievement:', error);
      // Revert if update fails
      setVisionItems(prev => prev.map(i => i.id === item.id ? item : i));
    }
  };

  const nextStep = () => {
    if (currentStep < formSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const toggleHabitSelection = (habitId) => {
    setFormData(prev => {
      const linkedHabits = [...prev.linked_habits];
      const index = linkedHabits.indexOf(habitId);
      if (index === -1) {
        linkedHabits.push(habitId);
      } else {
        linkedHabits.splice(index, 1);
      }
      return {...prev, linked_habits: linkedHabits};
    });
  };

  // Helper functions for form arrays
  const addPredictedAction = () => {
    setFormData(prev => ({
      ...prev,
      predicted_actions: [...prev.predicted_actions, { id: Date.now(), text: '', completed: false }]
    }));
  };

  const removePredictedAction = (index) => {
    setFormData(prev => ({
      ...prev,
      predicted_actions: prev.predicted_actions.filter((_, i) => i !== index)
    }));
  };

  const updatePredictedAction = (index, text) => {
    setFormData(prev => ({
      ...prev,
      predicted_actions: prev.predicted_actions.map((action, i) => 
        i === index ? { ...action, text } : action
      )
    }));
  };

  const addPredictedObstacle = () => {
    setFormData(prev => ({
      ...prev,
      predicted_obstacles: [...prev.predicted_obstacles, { id: Date.now(), text: '', mitigated: false }]
    }));
  };

  const removePredictedObstacle = (index) => {
    setFormData(prev => ({
      ...prev,
      predicted_obstacles: prev.predicted_obstacles.filter((_, i) => i !== index)
    }));
  };

  const updatePredictedObstacle = (index, text) => {
    setFormData(prev => ({
      ...prev,
      predicted_obstacles: prev.predicted_obstacles.map((obstacle, i) => 
        i === index ? { ...obstacle, text } : obstacle
      )
    }));
  };

  const getTimeRemaining = (targetDate) => {
    if (!targetDate) return '';
    try {
      const date = typeof targetDate === 'string' ? parseISO(targetDate) : targetDate;
      return formatDistanceToNow(date, {addSuffix: true});
    } catch (err) {
      return '';
    }
  };

  const addJourneyEntry = async (type) => {
    if (!selectedVision) return;

    try {
      const formToUse = type === 'action' ? actionFormData : setbackFormData;
      
      // Validate required fields
      if (!formToUse.text.trim()) {
        alert('Please enter a description');
        return;
      }

      // Create the new entry
      const newEntry = {
        id: Date.now(),
        type,
        ...formToUse,
        date: format(new Date(), 'yyyy-MM-dd')
      };

      // Update the vision item with the new journey entry
      const updatedVision = {
        ...selectedVision,
        journey_entries: [...(selectedVision.journey_entries || []), newEntry]
      };

      // Calculate new progress if applicable
      if (type === 'action') {
        // Potentially update progress based on actions
        const currentProgress = selectedVision.progress || 0;
        if (currentProgress < 100) {
          // Simple increment - in real app this would be more sophisticated
          updatedVision.progress = Math.min(100, currentProgress + 5);
        }
      }

      // Update UI optimistically
      setVisionItems(prev => 
        prev.map(item => item.id === selectedVision.id ? updatedVision : item)
      );
      setSelectedVision(updatedVision);

      // Reset form data
      if (type === 'action') {
        setActionFormData({
          text: '',
          mood: 'good',
          energy_level: 5,
          confidence: 5,
          notes: '',
          date: format(new Date(), 'yyyy-MM-dd')
        });
        setShowActionForm(false);
      } else {
        setSetbackFormData({
          text: '',
          mood: 'challenging',
          impact_level: 3,
          lesson_learned: '',
          recovery_plan: '',
          date: format(new Date(), 'yyyy-MM-dd')
        });
        setShowSetbackForm(false);
      }

      // Save to backend
      await visionBoardAPI.updateVisionItem(selectedVision.id, updatedVision);
    } catch (error) {
      console.error('Error adding journey entry:', error);
      // Revert if update fails
      loadData();
    }
  };

  const filteredItems = visionItems.filter(item => 
    selectedCategory === 'all' || item.category === selectedCategory
  );

  const getCategoryColor = (category) => {
    const colors = {
      personal: 'from-purple-400 to-violet-600',
      career: 'from-blue-400 to-indigo-600',
      health: 'from-emerald-400 to-green-600',
      relationships: 'from-pink-400 to-rose-600',
      financial: 'from-forge-400 to-orange-500',
      creative: 'from-indigo-400 to-purple-600'
    };
    return colors[category] || 'from-forge-400 to-forge-600';
  };

  const getCategoryBgColor = (category) => {
    const colors = {
      personal: 'bg-purple-500/20 text-purple-400 border-purple-400/30',
      career: 'bg-blue-500/20 text-blue-400 border-blue-400/30',
      health: 'bg-emerald-500/20 text-emerald-400 border-emerald-400/30',
      relationships: 'bg-pink-500/20 text-pink-400 border-pink-400/30',
      financial: 'bg-forge-500/20 text-forge-400 border-forge-400/30',
      creative: 'bg-indigo-500/20 text-indigo-400 border-indigo-400/30'
    };
    return colors[category] || 'bg-forge-500/20 text-forge-400 border-forge-400/30';
  };

  const getTimeframeIcon = (timeframe) => {
    const icons = {
      short: FiClock,
      medium: FiCalendar,
      long: FiTarget
    };
    return icons[timeframe] || FiCalendar;
  };

  const getTimeframeLabel = (timeframe) => {
    const labels = {
      short: 'Short-term',
      medium: 'Medium-term',
      long: 'Long-term'
    };
    return labels[timeframe] || 'Medium-term';
  };

  const getLinkedHabits = (habitIds) => {
    if (!habitIds || habitIds.length === 0) return [];
    return habits.filter(habit => habitIds.includes(habit.id));
  };

  const getMoodData = (moodId) => {
    return moods.find(m => m.id === moodId) || moods[2]; // Default to neutral
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-dominance-gradient flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-forge-400/30 border-t-forge-400 rounded-full animate-spin mb-4 mx-auto"></div>
          <h3 className="text-xl font-bold text-forge-400 mb-2">Loading Vision Board</h3>
          <p className="text-forge-300">Preparing your dreams...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dominance-gradient">
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Header - Mobile Optimized */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="flex items-center justify-center space-x-2 mb-3 sm:mb-4">
            <SafeIcon icon={FiEye} className="text-xl sm:text-2xl text-forge-400" />
            <h1 className="text-2xl sm:text-3xl font-bold bg-forge-gradient bg-clip-text text-transparent">
              Vision Board
            </h1>
          </div>
          <p className="text-forge-300 mb-4 sm:mb-6 px-2 text-sm sm:text-base">
            Visualize your dreams and track your progress
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="w-full sm:w-auto bg-forge-gradient text-shadow-950 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-semibold hover:shadow-power transition-all duration-300 text-sm sm:text-base"
          >
            <div className="flex items-center justify-center space-x-2">
              <SafeIcon icon={FiPlus} />
              <span>Add Vision</span>
            </div>
          </button>
        </div>

        {/* Category Filter - Mobile Optimized */}
        <div className="bg-gradient-to-r from-shadow-900/90 to-shadow-800/90 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-power border border-forge-400/30 p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex flex-wrap gap-1.5 sm:gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition-all duration-300 text-xs sm:text-sm ${
                  selectedCategory === category.id
                    ? 'bg-forge-gradient text-shadow-950 shadow-forge'
                    : 'bg-shadow-800/50 text-forge-300 hover:text-forge-400 hover:bg-shadow-700/50'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Vision Items - Mobile Optimized */}
        {filteredItems.length === 0 ? (
          <div className="text-center py-12 sm:py-16 px-4">
            <div className="w-16 sm:w-20 h-16 sm:h-20 bg-forge-gradient rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-power">
              <SafeIcon icon={FiEye} className="text-shadow-950 text-xl sm:text-2xl" />
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-forge-400 mb-3 sm:mb-4">Start Your Vision</h3>
            <p className="text-forge-300 mb-6 sm:mb-8 text-sm sm:text-base">
              Create your first vision to begin manifesting your dreams.
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="bg-forge-gradient text-shadow-950 px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl font-semibold hover:shadow-power transition-all duration-300 text-sm sm:text-base"
            >
              Create First Vision
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
            {filteredItems.map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-gradient-to-br from-shadow-900/90 to-shadow-800/90 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-power border border-forge-400/30 overflow-hidden hover:shadow-dominance transition-all duration-300 flex flex-col h-full ${
                  item.is_achieved ? 'ring-2 ring-emerald-400' : ''
                }`}
              >
                {/* Achievement Badge */}
                {item.is_achieved && (
                  <div className="bg-gradient-to-r from-emerald-400 to-green-500 text-white px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} />
                      <span>Achieved!</span>
                    </div>
                    <span className="text-xs bg-white/20 px-2 py-1 rounded-full">
                      100% Complete
                    </span>
                  </div>
                )}

                {/* Vision Content */}
                <div className="p-3 sm:p-5 flex-1 flex flex-col">
                  {/* Header with category and importance */}
                  <div className="flex items-start justify-between mb-3 sm:mb-4">
                    <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium border ${getCategoryBgColor(item.category)}`}>
                      {item.category}
                    </span>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <SafeIcon
                          key={i}
                          icon={FiStar}
                          className={`text-xs ${i < (item.importance || 3) ? 'text-forge-400' : 'text-gray-400'}`}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Type-specific content */}
                  {item.type === 'image' && item.image_url && (
                    <div className="mb-3 sm:mb-4">
                      <img
                        src={item.image_url}
                        alt={item.title}
                        className="w-full h-24 sm:h-32 object-cover rounded-lg mb-2 sm:mb-3"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=800&auto=format&fit=crop';
                        }}
                      />
                    </div>
                  )}
                  <h3 className="text-base sm:text-lg font-bold text-forge-400 mb-2">{item.title}</h3>
                  {item.type === 'affirmation' ? (
                    <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-gradient-to-r from-purple-900/20 to-indigo-900/20 rounded-lg border border-purple-400/30">
                      <p className="text-forge-200 text-sm sm:text-md italic">"{item.content}"</p>
                    </div>
                  ) : (
                    <p className="text-forge-300 mb-3 sm:mb-4 text-xs sm:text-sm line-clamp-3">{item.content}</p>
                  )}

                  {/* Progress Meter (for non-affirmations) */}
                  {item.type !== 'affirmation' && (
                    <div className="mb-3 sm:mb-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-forge-300">Progress</span>
                        <span className="text-xs font-bold text-forge-400">{item.progress || 0}%</span>
                      </div>
                      <div className="w-full bg-shadow-800/50 rounded-full h-2 sm:h-2.5">
                        <div
                          className={`h-2 sm:h-2.5 rounded-full bg-gradient-to-r ${getCategoryColor(item.category)}`}
                          style={{ width: `${item.progress || 0}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {/* Timeline */}
                  <div className="flex items-center space-x-2 mb-2 sm:mb-3 mt-auto">
                    <SafeIcon icon={getTimeframeIcon(item.timeframe)} className="text-forge-400 text-sm" />
                    <div>
                      <div className="text-xs text-forge-300">{getTimeframeLabel(item.timeframe)}</div>
                      <div className="text-xs sm:text-sm font-medium text-forge-400">
                        {getTimeRemaining(item.target_date)}
                      </div>
                    </div>
                  </div>

                  {/* Predicted Actions - Mobile Optimized */}
                  {item.predicted_actions && item.predicted_actions.length > 0 && (
                    <div className="mb-2 sm:mb-3">
                      <div className="flex items-center text-xs sm:text-sm text-forge-300 mb-1">
                        <SafeIcon icon={FiTarget} className="mr-1 text-xs" />
                        <span>Actions:</span>
                      </div>
                      <div className="space-y-1">
                        {item.predicted_actions.slice(0, 2).map((action, index) => (
                          <div
                            key={action.id || index}
                            className="text-xs bg-emerald-500/10 text-emerald-400 p-2 rounded border border-emerald-400/20"
                          >
                            {action.text}
                          </div>
                        ))}
                        {item.predicted_actions.length > 2 && (
                          <div className="text-xs text-emerald-400 font-medium">
                            +{item.predicted_actions.length - 2} more actions
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Predicted Obstacles - Mobile Optimized */}
                  {item.predicted_obstacles && item.predicted_obstacles.length > 0 && (
                    <div className="mb-2 sm:mb-3">
                      <div className="flex items-center text-xs sm:text-sm text-forge-300 mb-1">
                        <SafeIcon icon={FiAlertCircle} className="mr-1 text-xs" />
                        <span>Obstacles:</span>
                      </div>
                      <div className="space-y-1">
                        {item.predicted_obstacles.slice(0, 2).map((obstacle, index) => (
                          <div
                            key={obstacle.id || index}
                            className="text-xs bg-danger-500/10 text-danger-400 p-2 rounded border border-danger-400/20"
                          >
                            {obstacle.text}
                          </div>
                        ))}
                        {item.predicted_obstacles.length > 2 && (
                          <div className="text-xs text-danger-400 font-medium">
                            +{item.predicted_obstacles.length - 2} more obstacles
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Linked Habits - Mobile Optimized */}
                  {item.linked_habits && item.linked_habits.length > 0 && (
                    <div className="mb-2 sm:mb-3">
                      <div className="flex items-center text-xs sm:text-sm text-forge-300 mb-1">
                        <SafeIcon icon={FiLink} className="mr-1 text-xs" />
                        <span>Linked:</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {getLinkedHabits(item.linked_habits).slice(0, 2).map(habit => (
                          <span
                            key={habit.id}
                            className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full flex items-center border border-blue-400/30 max-w-20 sm:max-w-none"
                          >
                            <span className="mr-1">{habit.icon}</span>
                            <span className="truncate">{habit.title}</span>
                          </span>
                        ))}
                        {getLinkedHabits(item.linked_habits).length > 2 && (
                          <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full border border-blue-400/30">
                            +{getLinkedHabits(item.linked_habits).length - 2}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Purpose (Why) - Mobile Optimized */}
                  {item.purpose && (
                    <div className="mb-3 sm:mb-4 bg-gradient-to-r from-pink-900/20 to-purple-900/20 p-2 sm:p-3 rounded-lg border border-pink-400/30">
                      <div className="flex items-center text-purple-400 font-medium text-xs mb-1">
                        <SafeIcon icon={FiHeart} className="mr-1 text-xs" />
                        <span>Why This Matters</span>
                      </div>
                      <p className="text-purple-300 text-xs line-clamp-2">{item.purpose}</p>
                    </div>
                  )}

                  {/* Action Buttons - Mobile Optimized */}
                  <div className="flex gap-1 sm:gap-2 mt-auto pt-2 sm:pt-3 border-t border-forge-400/20">
                    <button
                      onClick={() => {
                        setSelectedVision(item);
                        setShowTimelineView(true);
                      }}
                      className="px-2 sm:px-3 py-1.5 sm:py-2 bg-blue-500/20 text-blue-400 border border-blue-400/30 rounded-lg text-xs font-medium hover:bg-blue-500/30 transition-all duration-200 flex items-center justify-center flex-1"
                    >
                      <SafeIcon icon={FiMap} className="mr-1 text-xs" />
                      <span className="hidden sm:inline">Journey</span>
                      <span className="sm:hidden">View</span>
                    </button>
                    <button
                      onClick={() => toggleAchieved(item)}
                      className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs font-medium transition-all duration-200 flex-1 flex items-center justify-center ${
                        item.is_achieved
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-400/30'
                          : 'bg-forge-500/20 text-forge-400 border border-forge-400/30 hover:bg-forge-500/30'
                      }`}
                    >
                      <SafeIcon icon={item.is_achieved ? FiCheck : FiTrendingUp} className="mr-1 text-xs" />
                      <span className="hidden sm:inline">{item.is_achieved ? 'Achieved' : 'Mark Complete'}</span>
                      <span className="sm:hidden">{item.is_achieved ? 'Done' : 'Complete'}</span>
                    </button>
                    <button
                      onClick={() => handleEdit(item)}
                      className="px-2 sm:px-3 py-1.5 sm:py-2 bg-purple-500/20 text-purple-400 border border-purple-400/30 rounded-lg text-xs font-medium hover:bg-purple-500/30 transition-all duration-200 flex items-center justify-center"
                    >
                      <SafeIcon icon={FiEdit2} className="text-xs" />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="px-2 sm:px-3 py-1.5 sm:py-2 bg-danger-500/20 text-danger-400 border border-danger-400/30 rounded-lg text-xs font-medium hover:bg-danger-500/30 transition-all duration-200 flex items-center justify-center"
                    >
                      <SafeIcon icon={FiTrash2} className="text-xs" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Vision Form Modal - Enhanced with Steps */}
        <AnimatePresence>
          {showForm && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
              <motion.div
                initial={{ opacity: 0, y: '100%' }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: '100%' }}
                className="bg-gradient-to-br from-shadow-900/95 to-shadow-800/95 backdrop-blur-sm w-full sm:max-w-4xl sm:rounded-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto border border-forge-400/30"
              >
                <div className="bg-forge-gradient p-4 sm:p-5 text-shadow-950 sm:rounded-t-2xl">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg sm:text-xl font-bold">
                      {editingItem ? 'Edit Vision' : 'Create Vision'}
                    </h2>
                    <button
                      onClick={() => {
                        setShowForm(false);
                        setEditingItem(null);
                        resetForm();
                      }}
                      className="p-2 hover:bg-shadow-900/20 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiX} />
                    </button>
                  </div>

                  {/* Step Progress */}
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    {formSteps.map((step, index) => (
                      <div key={index} className="flex items-center">
                        <div
                          className={`w-6 sm:w-8 h-6 sm:h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all duration-300 ${
                            index <= currentStep
                              ? 'bg-shadow-950 text-forge-400'
                              : 'bg-shadow-900/20 text-shadow-950/60'
                          }`}
                        >
                          {index + 1}
                        </div>
                        {index < formSteps.length - 1 && (
                          <div
                            className={`w-4 sm:w-8 h-0.5 mx-1 transition-all duration-300 ${
                              index < currentStep ? 'bg-shadow-950' : 'bg-shadow-900/20'
                            }`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="p-4 sm:p-6">
                  <AnimatePresence mode="wait">
                    {/* Step 1: Type Selection */}
                    {currentStep === 0 && (
                      <motion.div
                        key="step1"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-6"
                      >
                        <div className="text-center mb-6">
                          <SafeIcon icon={FiGrid} className="text-3xl text-forge-400 mx-auto mb-2" />
                          <h3 className="text-xl font-semibold text-forge-400">Choose Vision Type</h3>
                          <p className="text-forge-300">What type of vision are you creating?</p>
                        </div>
                        <div className="grid gap-4">
                          {visionTypes.map(type => (
                            <button
                              key={type.id}
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, type: type.id }))}
                              className={`p-4 rounded-2xl text-left transition-all duration-300 ${
                                formData.type === type.id
                                  ? 'bg-forge-gradient text-shadow-950 shadow-forge'
                                  : 'bg-shadow-800/50 hover:bg-shadow-700/50 border border-forge-400/30 text-forge-200'
                              }`}
                            >
                              <div className="flex items-center space-x-3 mb-2">
                                <SafeIcon icon={type.icon} className="text-xl" />
                                <span className="font-semibold">{type.name}</span>
                              </div>
                              <p className={`text-sm ${formData.type === type.id ? 'text-shadow-950/80' : 'text-forge-300'}`}>
                                {type.description}
                              </p>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}

                    {/* Step 2: Basic Details */}
                    {currentStep === 1 && (
                      <motion.div
                        key="step2"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-6"
                      >
                        <div className="text-center mb-6">
                          <SafeIcon icon={FiFileText} className="text-3xl text-forge-400 mx-auto mb-2" />
                          <h3 className="text-xl font-semibold text-forge-400">Vision Details</h3>
                          <p className="text-forge-300">Describe your vision clearly</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-forge-300 mb-2">
                            Vision Title *
                          </label>
                          <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                            className="w-full px-4 py-3 border border-forge-400/30 rounded-xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 bg-shadow-800/50 text-forge-200 placeholder-forge-300/50"
                            placeholder="Enter your vision title..."
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-forge-300 mb-2">
                            Description *
                          </label>
                          <textarea
                            value={formData.content}
                            onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                            rows="4"
                            className="w-full px-4 py-3 border border-forge-400/30 rounded-xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 bg-shadow-800/50 text-forge-200 placeholder-forge-300/50 resize-none"
                            placeholder="Describe your vision in detail..."
                            required
                          />
                        </div>

                        {formData.type === 'image' && (
                          <div>
                            <label className="block text-sm font-medium text-forge-300 mb-2">
                              Image URL
                            </label>
                            <input
                              type="url"
                              value={formData.image_url}
                              onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                              className="w-full px-4 py-3 border border-forge-400/30 rounded-xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 bg-shadow-800/50 text-forge-200 placeholder-forge-300/50"
                              placeholder="https://example.com/image.jpg"
                            />
                            {formData.image_url && (
                              <div className="mt-3">
                                <img
                                  src={formData.image_url}
                                  alt="Vision preview"
                                  className="w-full h-32 object-cover rounded-lg"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-forge-300 mb-2">
                              Category
                            </label>
                            <select
                              value={formData.category}
                              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                              className="w-full px-4 py-3 border border-forge-400/30 rounded-xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 bg-shadow-800/50 text-forge-200"
                            >
                              {categories.filter(c => c.id !== 'all').map(category => (
                                <option key={category.id} value={category.id}>
                                  {category.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-forge-300 mb-2">
                              Target Date
                            </label>
                            <input
                              type="date"
                              value={formData.target_date}
                              onChange={(e) => setFormData(prev => ({ ...prev, target_date: e.target.value }))}
                              className="w-full px-4 py-3 border border-forge-400/30 rounded-xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 bg-shadow-800/50 text-forge-200"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-forge-300 mb-2">
                            Timeframe
                          </label>
                          <div className="grid grid-cols-3 gap-3">
                            {timeframes.map(timeframe => (
                              <button
                                key={timeframe.id}
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, timeframe: timeframe.id }))}
                                className={`p-3 rounded-xl text-center transition-all duration-300 ${
                                  formData.timeframe === timeframe.id
                                    ? 'bg-forge-gradient text-shadow-950 shadow-forge'
                                    : 'bg-shadow-800/50 hover:bg-shadow-700/50 border border-forge-400/30 text-forge-200'
                                }`}
                              >
                                <SafeIcon icon={timeframe.icon} className="mx-auto mb-1" />
                                <div className="font-medium text-sm">{timeframe.name}</div>
                                <div className="text-xs opacity-80">{timeframe.description}</div>
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-forge-300 mb-2">
                            Importance Level
                          </label>
                          <div className="flex items-center space-x-2">
                            {[1, 2, 3, 4, 5].map(level => (
                              <button
                                key={level}
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, importance: level }))}
                                className="p-2"
                              >
                                <SafeIcon
                                  icon={FiStar}
                                  className={`text-xl ${level <= formData.importance ? 'text-forge-400' : 'text-gray-400'}`}
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Step 3: Purpose */}
                    {currentStep === 2 && (
                      <motion.div
                        key="step3"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-6"
                      >
                        <div className="text-center mb-6">
                          <SafeIcon icon={FiCompass} className="text-3xl text-forge-400 mx-auto mb-2" />
                          <h3 className="text-xl font-semibold text-forge-400">Why This Matters</h3>
                          <p className="text-forge-300">What's your deeper purpose behind this vision?</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-forge-300 mb-2">
                            Purpose & Meaning
                          </label>
                          <textarea
                            value={formData.purpose}
                            onChange={(e) => setFormData(prev => ({ ...prev, purpose: e.target.value }))}
                            rows="4"
                            className="w-full px-4 py-3 border border-forge-400/30 rounded-xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 bg-shadow-800/50 text-forge-200 placeholder-forge-300/50 resize-none"
                            placeholder="Why is this vision important to you? What deeper meaning does it have? How will achieving it impact your life?"
                          />
                        </div>

                        <div className="bg-gradient-to-r from-pink-900/20 to-purple-900/20 rounded-xl p-4 border border-pink-400/30">
                          <div className="flex items-center space-x-2 mb-2">
                            <SafeIcon icon={FiHeart} className="text-pink-400" />
                            <span className="font-medium text-pink-400">Purpose Examples</span>
                          </div>
                          <div className="text-sm text-pink-300 space-y-1">
                            <p>• To prove to myself that I can overcome challenges</p>
                            <p>• To become a role model for my children</p>
                            <p>• To gain confidence and self-respect</p>
                            <p>• To create financial security for my family</p>
                            <p>• To make a positive impact on others</p>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Step 4: Predict Path */}
                    {currentStep === 3 && (
                      <motion.div
                        key="step4"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-6"
                      >
                        <div className="text-center mb-6">
                          <SafeIcon icon={FiActivity} className="text-3xl text-forge-400 mx-auto mb-2" />
                          <h3 className="text-xl font-semibold text-forge-400">Plan Your Journey</h3>
                          <p className="text-forge-300">Predict the actions you'll take and obstacles you might face</p>
                        </div>

                        {/* Predicted Actions */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <label className="text-sm font-medium text-forge-300">
                              Predicted Actions
                            </label>
                            <button
                              type="button"
                              onClick={addPredictedAction}
                              className="px-3 py-1 bg-emerald-500/20 text-emerald-400 border border-emerald-400/30 rounded-lg text-sm hover:bg-emerald-500/30 transition-colors"
                            >
                              <SafeIcon icon={FiPlus} className="mr-1" />
                              Add Action
                            </button>
                          </div>
                          <div className="space-y-2">
                            {formData.predicted_actions.map((action, index) => (
                              <div key={index} className="flex gap-2">
                                <input
                                  type="text"
                                  value={action.text}
                                  onChange={(e) => updatePredictedAction(index, e.target.value)}
                                  className="flex-1 px-3 py-2 border border-emerald-400/30 rounded-lg focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 bg-shadow-800/50 text-forge-200 placeholder-forge-300/50 text-sm"
                                  placeholder="What action will you take?"
                                />
                                <button
                                  type="button"
                                  onClick={() => removePredictedAction(index)}
                                  className="px-2 py-2 text-danger-400 hover:bg-danger-400/20 rounded-lg transition-colors"
                                >
                                  <SafeIcon icon={FiMinusCircle} />
                                </button>
                              </div>
                            ))}
                            {formData.predicted_actions.length === 0 && (
                              <p className="text-sm text-forge-300/60 italic">
                                Add actions you plan to take to achieve this vision
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Predicted Obstacles */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <label className="text-sm font-medium text-forge-300">
                              Predicted Obstacles
                            </label>
                            <button
                              type="button"
                              onClick={addPredictedObstacle}
                              className="px-3 py-1 bg-danger-500/20 text-danger-400 border border-danger-400/30 rounded-lg text-sm hover:bg-danger-500/30 transition-colors"
                            >
                              <SafeIcon icon={FiPlus} className="mr-1" />
                              Add Obstacle
                            </button>
                          </div>
                          <div className="space-y-2">
                            {formData.predicted_obstacles.map((obstacle, index) => (
                              <div key={index} className="flex gap-2">
                                <input
                                  type="text"
                                  value={obstacle.text}
                                  onChange={(e) => updatePredictedObstacle(index, e.target.value)}
                                  className="flex-1 px-3 py-2 border border-danger-400/30 rounded-lg focus:ring-2 focus:ring-danger-400 focus:border-danger-400 bg-shadow-800/50 text-forge-200 placeholder-forge-300/50 text-sm"
                                  placeholder="What obstacle might you face?"
                                />
                                <button
                                  type="button"
                                  onClick={() => removePredictedObstacle(index)}
                                  className="px-2 py-2 text-danger-400 hover:bg-danger-400/20 rounded-lg transition-colors"
                                >
                                  <SafeIcon icon={FiMinusCircle} />
                                </button>
                              </div>
                            ))}
                            {formData.predicted_obstacles.length === 0 && (
                              <p className="text-sm text-forge-300/60 italic">
                                Add potential obstacles or challenges you might encounter
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="bg-gradient-to-r from-blue-900/20 to-indigo-900/20 rounded-xl p-4 border border-blue-400/30">
                          <div className="flex items-center space-x-2 mb-2">
                            <SafeIcon icon={FiTarget} className="text-blue-400" />
                            <span className="font-medium text-blue-400">Planning Benefits</span>
                          </div>
                          <div className="text-sm text-blue-300 space-y-1">
                            <p>• Predicting actions helps you stay focused</p>
                            <p>• Anticipating obstacles prepares you mentally</p>
                            <p>• Having a plan increases your success rate</p>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Step 5: Link Habits */}
                    {currentStep === 4 && (
                      <motion.div
                        key="step5"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-6"
                      >
                        <div className="text-center mb-6">
                          <SafeIcon icon={FiList} className="text-3xl text-forge-400 mx-auto mb-2" />
                          <h3 className="text-xl font-semibold text-forge-400">Link to Habits</h3>
                          <p className="text-forge-300">Connect this vision to your daily habits</p>
                        </div>

                        {habits.length > 0 ? (
                          <div className="grid gap-3">
                            {habits.map(habit => (
                              <button
                                key={habit.id}
                                type="button"
                                onClick={() => toggleHabitSelection(habit.id)}
                                className={`p-3 rounded-xl text-left transition-all duration-300 ${
                                  formData.linked_habits.includes(habit.id)
                                    ? 'bg-blue-500/20 text-blue-400 border border-blue-400/30'
                                    : 'bg-shadow-800/50 hover:bg-shadow-700/50 border border-forge-400/30 text-forge-200'
                                }`}
                              >
                                <div className="flex items-center space-x-3">
                                  <div className="text-xl">{habit.icon}</div>
                                  <div>
                                    <div className="font-medium">{habit.title}</div>
                                    <div className="text-sm opacity-70">{habit.description}</div>
                                  </div>
                                  {formData.linked_habits.includes(habit.id) && (
                                    <SafeIcon icon={FiCheck} className="ml-auto text-blue-400" />
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                              <SafeIcon icon={FiList} className="text-blue-400 text-xl" />
                            </div>
                            <h4 className="text-lg font-medium text-forge-400 mb-2">No Habits Yet</h4>
                            <p className="text-forge-300 mb-4">
                              You can create habits later and link them to this vision.
                            </p>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Navigation Buttons */}
                  <div className="flex justify-between items-center mt-8 pt-6 border-t border-forge-400/20">
                    <button
                      type="button"
                      onClick={prevStep}
                      disabled={currentStep === 0}
                      className="px-4 py-2 text-forge-300 hover:text-forge-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                    >
                      <SafeIcon icon={FiChevronLeft} />
                      <span>Previous</span>
                    </button>

                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowForm(false);
                          setEditingItem(null);
                          resetForm();
                        }}
                        className="px-6 py-3 border border-forge-400/30 text-forge-300 rounded-xl hover:bg-shadow-700/50 transition-colors"
                      >
                        Cancel
                      </button>
                      
                      {currentStep < formSteps.length - 1 ? (
                        <button
                          type="button"
                          onClick={nextStep}
                          disabled={currentStep === 1 && (!formData.title.trim() || !formData.content.trim())}
                          className="px-6 py-3 bg-forge-gradient text-shadow-950 rounded-xl hover:shadow-power disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center space-x-2"
                        >
                          <span>Next</span>
                          <SafeIcon icon={FiChevronRight} />
                        </button>
                      ) : (
                        <button
                          type="submit"
                          disabled={!formData.title.trim() || !formData.content.trim()}
                          className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                        >
                          {editingItem ? 'Update Vision' : 'Create Vision'}
                        </button>
                      )}
                    </div>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Timeline View Modal - Mobile Optimized */}
        <AnimatePresence>
          {showTimelineView && selectedVision && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
              <motion.div
                initial={{ opacity: 0, y: '100%' }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: '100%' }}
                className="bg-gradient-to-br from-shadow-900/95 to-shadow-800/95 backdrop-blur-sm w-full sm:max-w-4xl sm:rounded-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto border border-purple-400/30"
              >
                <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-4 sm:p-5 text-white sm:rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <SafeIcon icon={FiMap} className="text-lg sm:text-xl" />
                      <div>
                        <h2 className="text-lg sm:text-xl font-bold">{selectedVision.title}</h2>
                        <p className="text-purple-200 text-xs sm:text-sm">Vision Journey Timeline</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowTimelineView(false)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiX} />
                    </button>
                  </div>
                </div>
                
                <div className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3">
                    <div className="flex items-center space-x-2">
                      <div className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium border ${getCategoryBgColor(selectedVision.category)}`}>
                        {selectedVision.category}
                      </div>
                      <div className="text-sm text-forge-300">
                        <span className="text-purple-400 font-medium">{selectedVision.progress || 0}%</span> complete
                      </div>
                    </div>
                    
                    <div className="flex space-x-2 w-full sm:w-auto">
                      <button
                        onClick={() => setShowActionForm(true)}
                        className="flex-1 sm:flex-none px-3 py-2 bg-emerald-500/20 text-emerald-400 border border-emerald-400/30 rounded-lg text-xs font-medium hover:bg-emerald-500/30 transition-all duration-200 flex items-center justify-center"
                      >
                        <SafeIcon icon={FiPlus} className="mr-1" />
                        Action
                      </button>
                      <button
                        onClick={() => setShowSetbackForm(true)}
                        className="flex-1 sm:flex-none px-3 py-2 bg-danger-500/20 text-danger-400 border border-danger-400/30 rounded-lg text-xs font-medium hover:bg-danger-500/30 transition-all duration-200 flex items-center justify-center"
                      >
                        <SafeIcon icon={FiAlertCircle} className="mr-1" />
                        Setback
                      </button>
                    </div>
                  </div>
                  
                  {/* Timeline View - Mobile Optimized */}
                  <div className="relative pl-4 sm:pl-6 space-y-4 sm:space-y-6 mb-4 sm:mb-6">
                    {/* Timeline line */}
                    <div className="absolute top-0 bottom-0 left-2 sm:left-3 w-px bg-purple-500/30"></div>
                    
                    {selectedVision.journey_entries && selectedVision.journey_entries.length > 0 ? (
                      // Display journey entries
                      selectedVision.journey_entries
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .map((entry, index) => {
                          const moodData = getMoodData(entry.mood);
                          return (
                            <div key={entry.id || index} className="relative">
                              <div 
                                className={`absolute -left-4 sm:-left-6 w-4 sm:w-6 h-4 sm:h-6 ${
                                  entry.type === 'action' 
                                    ? 'bg-gradient-to-r from-emerald-400 to-green-500' 
                                    : 'bg-gradient-to-r from-danger-400 to-pink-500'
                                } rounded-full flex items-center justify-center`}
                              >
                                <SafeIcon 
                                  icon={entry.type === 'action' ? FiCheckCircle : FiXCircle} 
                                  className="text-white text-xs" 
                                />
                              </div>
                              
                              <div 
                                className={`bg-dreamforge-black/60 p-3 sm:p-4 rounded-xl border ${
                                  entry.type === 'action' 
                                    ? 'border-emerald-500/20' 
                                    : 'border-danger-500/20'
                                }`}
                              >
                                <div className="flex flex-col sm:flex-row justify-between mb-2 gap-2">
                                  <div className="flex items-center space-x-2">
                                    <span 
                                      className={`${
                                        entry.type === 'action' 
                                          ? 'text-emerald-400' 
                                          : 'text-danger-400'
                                      } font-medium text-sm`}
                                    >
                                      {entry.text}
                                    </span>
                                    <div 
                                      className={`w-5 h-5 rounded-full flex items-center justify-center ${
                                        entry.type === 'action' 
                                          ? 'bg-emerald-500/10' 
                                          : 'bg-danger-500/10'
                                      }`}
                                    >
                                      {moodData.emoji}
                                    </div>
                                  </div>
                                  <span 
                                    className={`text-xs px-2 py-1 rounded-full self-start ${
                                      entry.type === 'action' 
                                        ? 'bg-emerald-500/10 text-emerald-300' 
                                        : 'bg-danger-500/10 text-danger-300'
                                    }`}
                                  >
                                    {format(parseISO(entry.date), 'MMM d, yyyy')}
                                  </span>
                                </div>
                                
                                {/* Action-specific details */}
                                {entry.type === 'action' && (
                                  <div className="grid grid-cols-3 gap-2 mb-3">
                                    <div className="text-center p-2 bg-dreamforge-black/30 rounded-lg">
                                      <div className="text-xs text-forge-300 mb-1">Energy</div>
                                      <div className="text-sm font-semibold text-forge-400">{entry.energy_level}/10</div>
                                    </div>
                                    <div className="text-center p-2 bg-dreamforge-black/30 rounded-lg">
                                      <div className="text-xs text-forge-300 mb-1">Confidence</div>
                                      <div className="text-sm font-semibold text-forge-400">{entry.confidence}/10</div>
                                    </div>
                                    <div className="text-center p-2 bg-dreamforge-black/30 rounded-lg">
                                      <div className="text-xs text-forge-300 mb-1">Mood</div>
                                      <div className="text-sm font-semibold text-forge-400">{moodData.name}</div>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Setback-specific details */}
                                {entry.type === 'setback' && (
                                  <div className="space-y-2 mt-3">
                                    <div className="p-2 bg-dreamforge-black/30 rounded-lg">
                                      <div className="text-xs text-danger-300 mb-1">Impact Level</div>
                                      <div className="text-sm font-semibold text-danger-400">{entry.impact_level}/10</div>
                                    </div>
                                    {entry.lesson_learned && (
                                      <div className="p-2 bg-dreamforge-black/30 rounded-lg">
                                        <div className="text-xs text-forge-300 mb-1">Lesson Learned</div>
                                        <div className="text-sm text-forge-400">{entry.lesson_learned}</div>
                                      </div>
                                    )}
                                    {entry.recovery_plan && (
                                      <div className="p-2 bg-dreamforge-black/30 rounded-lg">
                                        <div className="text-xs text-blue-300 mb-1">Recovery Plan</div>
                                        <div className="text-sm text-blue-400">{entry.recovery_plan}</div>
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {entry.notes && (
                                  <div className="mt-3 text-sm text-forge-300 bg-dreamforge-black/30 p-3 rounded-lg">
                                    {entry.notes}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })
                    ) : (
                      <div className="text-center py-8 sm:py-10 px-4">
                        <div className="w-12 sm:w-16 h-12 sm:h-16 bg-purple-gradient rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                          <SafeIcon icon={FiMap} className="text-white text-lg sm:text-xl" />
                        </div>
                        <h3 className="text-base sm:text-lg font-bold text-forge-400 mb-2">Start Your Journey</h3>
                        <p className="text-forge-300 mb-4 sm:mb-6 text-sm">
                          Record your actions and setbacks to track your progress toward this vision
                        </p>
                        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 justify-center">
                          <button
                            onClick={() => setShowActionForm(true)}
                            className="px-4 py-2 bg-emerald-500/20 text-emerald-400 border border-emerald-400/30 rounded-lg text-sm font-medium hover:bg-emerald-500/30 transition-all duration-200 flex items-center justify-center"
                          >
                            <SafeIcon icon={FiPlus} className="mr-2" />
                            Record First Action
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Milestones Section - Mobile Optimized */}
                  {selectedVision.milestones && selectedVision.milestones.length > 0 && (
                    <div className="mt-6 sm:mt-8">
                      <h3 className="text-base sm:text-lg font-bold text-forge-400 mb-3 sm:mb-4">Milestone Progress</h3>
                      <div className="grid grid-cols-2 gap-3 sm:gap-4">
                        {milestoneTemplates.map(milestone => {
                          const unlockedMilestone = selectedVision.milestones.find(m => m.threshold === milestone.threshold && m.unlocked);
                          return (
                            <div 
                              key={milestone.threshold}
                              className={`p-3 sm:p-4 rounded-xl border text-center ${
                                unlockedMilestone
                                  ? 'bg-gradient-to-r ' + milestone.color + ' text-white'
                                  : 'bg-dreamforge-black/40 border-forge-400/20 text-forge-300'
                              }`}
                            >
                              <div className="text-xl sm:text-2xl mb-2">{milestone.icon}</div>
                              <div className="font-bold text-sm sm:text-base">{milestone.title}</div>
                              <div className="text-xs mt-1">
                                {unlockedMilestone
                                  ? format(parseISO(unlockedMilestone.date), 'MMM d, yyyy')
                                  : `${milestone.threshold}% complete`
                                }
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-end mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-forge-400/20">
                    <button
                      onClick={() => setShowTimelineView(false)}
                      className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-forge-gradient text-shadow-950 rounded-xl hover:shadow-power transition-all duration-300 text-sm sm:text-base"
                    >
                      Close Timeline
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
        
        {/* Action Form - Mobile Optimized */}
        <AnimatePresence>
          {showActionForm && selectedVision && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-[60]">
              <motion.div
                initial={{ opacity: 0, y: '100%' }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: '100%' }}
                className="bg-gradient-to-br from-shadow-900/95 to-shadow-800/95 backdrop-blur-sm w-full sm:max-w-lg sm:rounded-2xl max-h-[95vh] overflow-y-auto border border-emerald-400/30"
              >
                <div className="bg-gradient-to-r from-emerald-500 to-green-600 p-4 sm:p-5 text-white sm:rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <SafeIcon icon={FiCheckCircle} className="text-lg sm:text-xl" />
                      <h2 className="text-lg sm:text-xl font-bold">Record Action</h2>
                    </div>
                    <button
                      onClick={() => setShowActionForm(false)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiX} />
                    </button>
                  </div>
                </div>
                
                <div className="p-4 sm:p-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-forge-300 mb-2">
                        What action did you take? *
                      </label>
                      <input
                        type="text"
                        value={actionFormData.text}
                        onChange={(e) => setActionFormData(prev => ({ ...prev, text: e.target.value }))}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-emerald-400/30 rounded-xl focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 bg-shadow-800/50 text-forge-200 placeholder-forge-300/50 text-sm sm:text-base"
                        placeholder="Describe the action you took..."
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-forge-300 mb-2">
                        How did you feel about it?
                      </label>
                      <div className="grid grid-cols-5 gap-1 sm:gap-2">
                        {moods.map(mood => (
                          <button
                            key={mood.id}
                            type="button"
                            onClick={() => setActionFormData(prev => ({ ...prev, mood: mood.id }))}
                            className={`p-2 sm:p-3 rounded-xl text-center transition-all duration-300 ${
                              actionFormData.mood === mood.id
                                ? `bg-gradient-to-r ${mood.color} text-white shadow-lg`
                                : 'bg-shadow-800/50 hover:bg-shadow-700/50 border border-forge-400/20 text-forge-200'
                            }`}
                          >
                            <div className="text-lg sm:text-2xl mb-1">{mood.emoji}</div>
                            <div className="text-xs font-medium">{mood.name}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-forge-300 mb-2">
                          Energy Level (1-10)
                        </label>
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={actionFormData.energy_level}
                          onChange={(e) => setActionFormData(prev => ({ ...prev, energy_level: parseInt(e.target.value) }))}
                          className="w-full accent-emerald-500"
                        />
                        <div className="flex justify-between text-xs text-forge-300">
                          <span>Low</span>
                          <span className="text-emerald-400 font-bold">{actionFormData.energy_level}</span>
                          <span>High</span>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-forge-300 mb-2">
                          Confidence (1-10)
                        </label>
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={actionFormData.confidence}
                          onChange={(e) => setActionFormData(prev => ({ ...prev, confidence: parseInt(e.target.value) }))}
                          className="w-full accent-emerald-500"
                        />
                        <div className="flex justify-between text-xs text-forge-300">
                          <span>Low</span>
                          <span className="text-emerald-400 font-bold">{actionFormData.confidence}</span>
                          <span>High</span>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-forge-300 mb-2">
                        Notes (optional)
                      </label>
                      <textarea
                        value={actionFormData.notes}
                        onChange={(e) => setActionFormData(prev => ({ ...prev, notes: e.target.value }))}
                        rows="3"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-emerald-400/30 rounded-xl focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 bg-shadow-800/50 text-forge-200 placeholder-forge-300/50 resize-none text-sm sm:text-base"
                        placeholder="Any additional thoughts or observations..."
                      />
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-forge-400/20">
                    <button
                      type="button"
                      onClick={() => setShowActionForm(false)}
                      className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 border border-forge-400/30 text-forge-300 rounded-xl hover:bg-shadow-700/50 transition-colors text-sm sm:text-base"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => addJourneyEntry('action')}
                      disabled={!actionFormData.text.trim()}
                      className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl hover:shadow-lg transition-all duration-300 disabled:opacity-50 text-sm sm:text-base"
                    >
                      Save Action
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
        
        {/* Setback Form - Mobile Optimized */}
        <AnimatePresence>
          {showSetbackForm && selectedVision && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-[60]">
              <motion.div
                initial={{ opacity: 0, y: '100%' }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: '100%' }}
                className="bg-gradient-to-br from-shadow-900/95 to-shadow-800/95 backdrop-blur-sm w-full sm:max-w-lg sm:rounded-2xl max-h-[95vh] overflow-y-auto border border-danger-400/30"
              >
                <div className="bg-gradient-to-r from-danger-500 to-pink-600 p-4 sm:p-5 text-white sm:rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <SafeIcon icon={FiXCircle} className="text-lg sm:text-xl" />
                      <h2 className="text-lg sm:text-xl font-bold">Record Setback</h2>
                    </div>
                    <button
                      onClick={() => setShowSetbackForm(false)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiX} />
                    </button>
                  </div>
                </div>
                
                <div className="p-4 sm:p-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-forge-300 mb-2">
                        What setback occurred? *
                      </label>
                      <input
                        type="text"
                        value={setbackFormData.text}
                        onChange={(e) => setSetbackFormData(prev => ({ ...prev, text: e.target.value }))}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-danger-400/30 rounded-xl focus:ring-2 focus:ring-danger-400 focus:border-danger-400 bg-shadow-800/50 text-forge-200 placeholder-forge-300/50 text-sm sm:text-base"
                        placeholder="Describe the setback you encountered..."
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-forge-300 mb-2">
                        How did you feel about it?
                      </label>
                      <div className="grid grid-cols-5 gap-1 sm:gap-2">
                        {moods.map(mood => (
                          <button
                            key={mood.id}
                            type="button"
                            onClick={() => setSetbackFormData(prev => ({ ...prev, mood: mood.id }))}
                            className={`p-2 sm:p-3 rounded-xl text-center transition-all duration-300 ${
                              setbackFormData.mood === mood.id
                                ? `bg-gradient-to-r ${mood.color} text-white shadow-lg`
                                : 'bg-shadow-800/50 hover:bg-shadow-700/50 border border-forge-400/20 text-forge-200'
                            }`}
                          >
                            <div className="text-lg sm:text-2xl mb-1">{mood.emoji}</div>
                            <div className="text-xs font-medium">{mood.name}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-forge-300 mb-2">
                        Impact Level (1-10)
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={setbackFormData.impact_level}
                        onChange={(e) => setSetbackFormData(prev => ({ ...prev, impact_level: parseInt(e.target.value) }))}
                        className="w-full accent-danger-500"
                      />
                      <div className="flex justify-between text-xs text-forge-300">
                        <span>Minor</span>
                        <span className="text-danger-400 font-bold">{setbackFormData.impact_level}</span>
                        <span>Major</span>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-forge-300 mb-2">
                        Lesson Learned
                      </label>
                      <textarea
                        value={setbackFormData.lesson_learned}
                        onChange={(e) => setSetbackFormData(prev => ({ ...prev, lesson_learned: e.target.value }))}
                        rows="2"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-danger-400/30 rounded-xl focus:ring-2 focus:ring-danger-400 focus:border-danger-400 bg-shadow-800/50 text-forge-200 placeholder-forge-300/50 resize-none text-sm sm:text-base"
                        placeholder="What did you learn from this setback?"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-forge-300 mb-2">
                        Recovery Plan
                      </label>
                      <textarea
                        value={setbackFormData.recovery_plan}
                        onChange={(e) => setSetbackFormData(prev => ({ ...prev, recovery_plan: e.target.value }))}
                        rows="2"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-danger-400/30 rounded-xl focus:ring-2 focus:ring-danger-400 focus:border-danger-400 bg-shadow-800/50 text-forge-200 placeholder-forge-300/50 resize-none text-sm sm:text-base"
                        placeholder="How will you overcome this setback?"
                      />
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-forge-400/20">
                    <button
                      type="button"
                      onClick={() => setShowSetbackForm(false)}
                      className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 border border-forge-400/30 text-forge-300 rounded-xl hover:bg-shadow-700/50 transition-colors text-sm sm:text-base"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => addJourneyEntry('setback')}
                      disabled={!setbackFormData.text.trim()}
                      className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-danger-500 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all duration-300 disabled:opacity-50 text-sm sm:text-base"
                    >
                      Save Setback
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default VisionBoard;